import {ServerError} from '@yandex-delivery/core/utils/network'
import {delay, raise} from '@yandex-delivery/utils'

import type {PaymentCard} from '../../model/payment'
import type {CompanyInfoForm, TicketStateResponse} from '../../repo/cargo-crm/types'
import {getDisableReason, getNewTicketToken, getPartialCompanyInfo, getUnresolvedTicketID} from './getters/state'
import {
  getCardID,
  getClientID,
  getNextStepToken,
  isNotCardBoundFormStep,
  isNotCompanyInfoFormStep,
} from './getters/ticket-state'
import {toDefaultEmail} from './mappers/toDefaultEmail'
import {toTicketFlow, toUserSteps} from './mappers/toTicketFlow'
import type {
  ClientID,
  PollTicketState,
  RegistrationRequest,
  RegistrationResponse,
  RegistrationService,
  RegistrationServiceParams,
  TicketFlow,
  TicketID,
} from './types'

function fetchDefaultEmail(params: RegistrationServiceParams): Promise<string | undefined> {
  const {fetchAllAccounts} = params.passportRepository

  return fetchAllAccounts()
    .then(toDefaultEmail)
    .then(email => email || undefined)
    .catch(() => undefined)
}

// todo задачка на будущее - подумать как одним интерфейсом объединить нормальную регу и скрытую
export function createRegistrationService(params: RegistrationServiceParams): RegistrationService {
  const {fetchFillCompanyInfo, fetchTicketState, fetchTicketInit, fetchState} = params.crmRepository
  const {fetchPaymentMethodSelect, fetchEmployeeCardBound} = params.corpRepository

  const pollTicketState: PollTicketState = function pollTicketState(params) {
    const {ticketID, predicate} = params

    function _pollTicketState(): Promise<TicketStateResponse> {
      return fetchTicketState({ticket_id: ticketID}).then(response => {
        return predicate(response) ? response : delay(2000).then(_pollTicketState)
      })
    }

    return _pollTicketState().then(toTicketFlow(ticketID))
  }

  // основная функция, которая поллит ticket/state до тех пор, пока не встретит известные для UI шаги
  function fetchTicketFlow(ticketID: TicketID): Promise<TicketFlow> {
    return pollTicketState({
      ticketID,
      predicate: toUserSteps,
    })
  }

  function fetchCreateCompany(
    ticketID: TicketID,
    token: string,
    companyInfoForm: CompanyInfoForm,
  ): Promise<TicketFlow> {
    return (
      fetchFillCompanyInfo({operation_token: token, company_info_form: companyInfoForm}, {ticket_id: ticketID})
        // после заполнения данных о компании awaited_steps не сразу меняется на следующие шаги
        // поэтому ждем пока идет переход на следующие шаги, и только потом уже выполняем общий поллинг
        // который умеет переключать нас на известный UI
        .then(() =>
          pollTicketState({
            ticketID,
            predicate: isNotCompanyInfoFormStep,
          }),
        )
        .then(() => fetchTicketFlow(ticketID))
    )
  }

  return {
    fetchRegistration(body: RegistrationRequest): Promise<RegistrationResponse> {
      let companyInfo: CompanyInfoForm = body

      return (
        fetchState()
          .then(async response => {
            companyInfo = Object.assign(companyInfo, getPartialCompanyInfo(response))

            if (!companyInfo.email) {
              // фоллбек на email из паспорта, если state ничего не вернул
              companyInfo.email = await fetchDefaultEmail(params)
            }

            return response
          })
          .then(response => {
            const unresolvedTicketID = getUnresolvedTicketID(response)
            const disableReason = getDisableReason(response)

            if (unresolvedTicketID) {
              return {
                ticket_id: unresolvedTicketID,
              }
            }

            if (disableReason) {
              throw new ServerError({
                body: disableReason,
                status: 9_409,
                // @ts-expect-error кривая типизация у ошибки
                error: new Error(disableReason.message),
              })
            }

            return fetchTicketInit(
              {operation_token: getNewTicketToken(response) || raise('new_ticket.token not found')},
              {
                corp_payment_type: 'card',
                registration_source: 'landing-order',
              },
            )
          })
          // после начала процесса регистрации, выполняем общий поллинг, который умеет переключать нас на известный UI
          .then(response => fetchTicketFlow(response.ticket_id))
          .then(response => {
            const clientID = getClientID(response.ticketState)

            if (clientID) {
              return response
            }

            return fetchCreateCompany(
              response.ticketID,
              getNextStepToken(response.ticketState) || raise('next_step.token not found'),
              companyInfo,
            )
          })
          .then(response => ({
            cardID: getCardID(response.ticketState),
            clientID: getClientID(response.ticketState) || raise('corp_client_id not found'),
            ticketID: response.ticketID,
          }))
      )
    },
    fetchCardBound(card: PaymentCard, country?: string, isNotificationsAllowed?: boolean) {
      const {id: nextCardID} = card

      function toCard(): PaymentCard {
        return card
      }

      function waitForCardBound(ticketID: TicketID) {
        return function _waitForCardBound(): Promise<unknown> {
          // после заполнения данных о компании awaited_steps не сразу меняется на следующие шаги
          // поэтому ждем пока идет переход на следующие шаги, и только потом уже выполняем общий поллинг
          // который умеет переключать нас на известный UI
          return pollTicketState({
            ticketID,
            predicate: isNotCardBoundFormStep,
          }).then(() => fetchTicketFlow(ticketID))
        }
      }

      function waitForCardSelect(clientID: ClientID) {
        return function _waitForCardSelect(): Promise<unknown> {
          return fetchPaymentMethodSelect(clientID, {selected_payment_method_kind: 'card'})
        }
      }

      return function _fetchCardBound(response: RegistrationResponse): Promise<PaymentCard> {
        const {ticketID, clientID, cardID: prevCardID} = response

        // если зарегистрироваться получилось, то привязываем карту
        return (
          fetchEmployeeCardBound(
            clientID,
            {
              card: {id: nextCardID},
              scopes: [{kind: 'agent_scheme'}],
              is_marketing_notifications_allowed: isNotificationsAllowed,
            },
            country ? {country} : undefined,
          )
            // если из скрытой регистрации вернули карту, значит пользователь пришел повторно
            // и нужно не привязывать карту, а выбрать ее
            .then(prevCardID ? waitForCardSelect(clientID) : waitForCardBound(ticketID))
            .then(toCard)
        )
      }
    },
  }
}
